import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { produce } from 'immer';
import { Observable } from 'rxjs';

import { ColumnId } from '@blaze-shared/api-contracts-domain';
import { BlazeTable, BlazeTableActionType } from '@blaze-shared/datasets-domain';
import { DatasetColumn, DatasetLinkValue } from '@blaze-shared/schema-domain';
import { deepClone } from '@blaze-shared/shared-util';

export interface BlazeTableState {
  actionType: BlazeTableActionType;
  sourceColumn: DatasetColumn | null;
  /** Used to count how many actions are in progress to know when to properly terminate listeners */
  actionsInProgress: number;
  pendingChangesForColumnEdit: boolean;
  updateRecordsRequest: BlazeTableUpdateRecords | null;
  deleteColumnRequest: ColumnId | null;
}

const initialState: BlazeTableState = {
  actionType: BlazeTableActionType.Noop,
  sourceColumn: null,
  actionsInProgress: 0,
  pendingChangesForColumnEdit: false,
  updateRecordsRequest: null,
  deleteColumnRequest: null,
};

export interface BlazeTableRecord {
  displayColumn: string;
  displayValue: string;
  value: DatasetLinkValue | { display: DatasetLinkValue; rowId: string } | string;
  tableHasExpandableLinks: boolean;
  otherLinkedValues?: [string, any][];
}

export interface BlazeTableUpdateRecords {
  column: DatasetColumn | undefined;
  config: BlazeTable | undefined;
}

@Injectable({ providedIn: 'root' })
export class BlazeTableStore extends ComponentStore<BlazeTableState> {
  // Read
  readonly actionType$: Observable<BlazeTableActionType> = this.select((state) => state.actionType);
  readonly sourceColumn$: Observable<DatasetColumn | null> = this.select(
    (state) => state.sourceColumn,
  );
  readonly actionsInProgress$: Observable<number> = this.select((state) => state.actionsInProgress);
  readonly pendingChangesForColumnEdit$: Observable<boolean> = this.select(
    (state) => state.pendingChangesForColumnEdit,
  );

  readonly updateRecords$: Observable<BlazeTableUpdateRecords | null> = this.select(
    (state) => state.updateRecordsRequest,
  );
  readonly deleteColumn$: Observable<ColumnId | null> = this.select(
    (state) => state.deleteColumnRequest,
  );

  setPendingChanges = this.updater((state, pendingChangesForColumnEdit: boolean) => {
    return { ...state, pendingChangesForColumnEdit };
  });

  // Add
  setActionType = this.updater((state, actionType: BlazeTableActionType) => {
    return { ...state, actionType };
  });
  setSourceColumn = this.updater((state, column: DatasetColumn | null) => {
    return { ...deepClone(state), sourceColumn: deepClone(column) };
  });

  // Delete
  readonly deleteActionType = this.updater((state) => {
    return { ...state, actionType: BlazeTableActionType.Noop };
  });
  readonly deleteSourceColumn = this.updater((state) => {
    return { ...state, sourceColumn: null };
  });

  // Actions in progress counter
  incrementActionsInProgress = this.updater((state) =>
    produce(state, (draft) => {
      draft.actionsInProgress++;
    }),
  );
  decrementActionsInProgress = this.updater((state) =>
    produce(state, (draft) => {
      draft.actionsInProgress--;
    }),
  );

  requestRecordsUpdate = this.updater((state, updateRecordsRequest: BlazeTableUpdateRecords) => {
    return { ...state, updateRecordsRequest };
  });

  clearDeleteColumnRequest = this.updater((state) => {
    return { ...state, deleteColumnRequest: null };
  });

  requestDeleteColumn = this.updater((state, columnId: ColumnId) => {
    return { ...state, deleteColumnRequest: columnId };
  });

  constructor() {
    super(initialState);
  }
}
